// Hub-screen tour mocks.
//
// Hub screens (CultivationHub, ScheduledActivities, etc.) render an
// aggregation of widgets that each fetch via axios. For tours on these
// screens we use the network-level interceptor pattern rather than
// reducer-level SET_RECORD dispatches (which is the detail-screen path).
// See sopui/src/helpers/tourInterceptor.js for the wiring.
//
// Per-tour scope is "awareness, not lifecycle" — one mock dataset for the
// whole tour, no per-step variation. If a future tour on the same screen
// needs lifecycle-stepped data (e.g. "this is what the hub looks like at
// month 1 vs month 6"), revisit by either:
//   - swapping the mock handlers to read stepNumber from
//     state.help.activeTourStep (would require publishing it from
//     guideHelper), or
//   - splitting the registration by tour identity, not just screen identity.

import { registerTourMocks } from '../helpers/tourInterceptor';
import common from '../jsons/common.json';

// ---------- Cultivation Hub ---------------------------------------------
//
// Endpoints the CultivationHub component issues (see CultivationHub.jsx
// around line 129-163):
//   GET /api/roomsummary                — location → rooms → items → batches tree
//   GET /api/cultivationbatch/list      — paged list { data, totalCount }
//   GET /api/batchactivity/list         — paged list of activities
//   GET /api/schedule/list              — paged schedule entries
//   GET /api/dtcprocessing              — { dryingOrders, trimmingOrders, curings }
//
// Shapes follow the real responses from sopkoa — the widgets read into
// nested fields (strain.name, batch.strain.name, item.name, etc.), so
// every level needs the right keys even if the values are placeholder.

const STRAIN_LH  = { _id: 'mock-hub-strain-lh',  name: 'Lemon Haze',          code: 'LH' };
const STRAIN_BD  = { _id: 'mock-hub-strain-bd',  name: 'Blue Dream',          code: 'BD' };
const STRAIN_GG4 = { _id: 'mock-hub-strain-gg4', name: 'Gorilla Glue #4',     code: 'GG4' };
const STRAIN_NL  = { _id: 'mock-hub-strain-nl',  name: 'Northern Lights',     code: 'NL' };

const ITEM_CLONE = { _id: 'mock-hub-item-clone', name: 'Rooted Clone' };
const ITEM_VEG   = { _id: 'mock-hub-item-veg',   name: 'Veg Plant' };
const ITEM_FLO   = { _id: 'mock-hub-item-flo',   name: 'Flowering Plant' };

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString();
const daysAhead = (n) => new Date(Date.now() + n * 86400000).toISOString();

// Batch mocks — used in the hub batches widget and referenced from rooms.
const mockBatches = [
    { _id: 'mock-hub-b-001', number: 'CB-2026-W04-LH', description: 'Lemon Haze 2026-W04 Run',
      strain: STRAIN_LH, strainSel: { value: STRAIN_LH._id, label: STRAIN_LH.name },
      status: 'growing', statusSel: { value: 'growing', label: 'Growing' },
      stage: 'flower', stageSel: { value: 'flower', label: 'Flower' },
      startQuantity: 120, currentQuantity: 116,
      plannedStart: daysAgo(56), actualStart: daysAgo(55),
      expectedHarvest: daysAhead(35),
      currentRooms: [{ room: { _id: 'mock-hub-r-fl-1', name: 'Flower Room 1' } }],
      plantTypeCounts: { flower: 116 } },
    { _id: 'mock-hub-b-002', number: 'CB-2026-W05-BD', description: 'Blue Dream 2026-W05 Run',
      strain: STRAIN_BD, strainSel: { value: STRAIN_BD._id, label: STRAIN_BD.name },
      status: 'growing', statusSel: { value: 'growing', label: 'Growing' },
      stage: 'veg', stageSel: { value: 'veg', label: 'Vegetation' },
      startQuantity: 100, currentQuantity: 100,
      plannedStart: daysAgo(21), actualStart: daysAgo(20),
      expectedHarvest: daysAhead(70),
      currentRooms: [{ room: { _id: 'mock-hub-r-vg-1', name: 'Veg Room 1' } }],
      plantTypeCounts: { veg: 100 } },
    { _id: 'mock-hub-b-003', number: 'CB-2026-W06-GG4', description: 'GG4 2026-W06 Run',
      strain: STRAIN_GG4, strainSel: { value: STRAIN_GG4._id, label: STRAIN_GG4.name },
      status: 'growing', statusSel: { value: 'growing', label: 'Growing' },
      stage: 'flower', stageSel: { value: 'flower', label: 'Flower' },
      startQuantity: 96, currentQuantity: 94,
      plannedStart: daysAgo(70), actualStart: daysAgo(69),
      expectedHarvest: daysAhead(21),
      currentRooms: [{ room: { _id: 'mock-hub-r-fl-2', name: 'Flower Room 2' } }],
      plantTypeCounts: { flower: 94 } },
    { _id: 'mock-hub-b-004', number: 'CB-2026-W07-NL', description: 'Northern Lights 2026-W07 Run',
      strain: STRAIN_NL, strainSel: { value: STRAIN_NL._id, label: STRAIN_NL.name },
      status: 'growing', statusSel: { value: 'growing', label: 'Growing' },
      stage: 'clone', stageSel: { value: 'clone', label: 'Propagation' },
      startQuantity: 144, currentQuantity: 144,
      plannedStart: daysAgo(7), actualStart: daysAgo(6),
      expectedHarvest: daysAhead(84),
      currentRooms: [{ room: { _id: 'mock-hub-r-pp-1', name: 'Prop Room 1' } }],
      plantTypeCounts: { clone: 144 } }
];

// Room summary — nested location → room → item → batches tree. The hub
// flattens it for the plant-totals widget and the room-grid.
const mockRoomSummary = [
    {
        _id: 'mock-hub-loc-bldg-b',
        name: 'Main Facility — Building B',
        rooms: [
            {
                _id: 'mock-hub-r-pp-1', name: 'Prop Room 1', plantCount: 144,
                items: [{ item: ITEM_CLONE, batches: [
                    { batch: { _id: mockBatches[3]._id, number: mockBatches[3].number, strain: STRAIN_NL }, item: ITEM_CLONE, count: 144 }
                ]}]
            },
            {
                _id: 'mock-hub-r-vg-1', name: 'Veg Room 1', plantCount: 100,
                items: [{ item: ITEM_VEG, batches: [
                    { batch: { _id: mockBatches[1]._id, number: mockBatches[1].number, strain: STRAIN_BD }, item: ITEM_VEG, count: 100 }
                ]}]
            },
            {
                _id: 'mock-hub-r-fl-1', name: 'Flower Room 1', plantCount: 116,
                items: [{ item: ITEM_FLO, batches: [
                    { batch: { _id: mockBatches[0]._id, number: mockBatches[0].number, strain: STRAIN_LH }, item: ITEM_FLO, count: 116 }
                ]}]
            },
            {
                _id: 'mock-hub-r-fl-2', name: 'Flower Room 2', plantCount: 94,
                items: [{ item: ITEM_FLO, batches: [
                    { batch: { _id: mockBatches[2]._id, number: mockBatches[2].number, strain: STRAIN_GG4 }, item: ITEM_FLO, count: 94 }
                ]}]
            }
        ]
    }
];

// Activities are grouped by `activityDefinition.description` in the hub's
// Batch Activities widget — one tile per distinct definition with the
// count of activities sharing that definition. Distribution and tile
// counts below mirror a representative real-tenant snapshot (53 total
// activities across 8 definitions) so the widget looks substantial
// rather than the sparse 4-tile mock we had earlier.
const DEFS = {
    HARVEST:  { _id: 'mock-def-harvest',  description: 'Harvest Plants' },
    TRANSPL:  { _id: 'mock-def-transpl',  description: 'Transplant' },
    ROOMXFR:  { _id: 'mock-def-roomxfr',  description: 'Room Transfer' },
    CLONEINS: { _id: 'mock-def-cloneins', description: 'Clone Inspection and Feeding' },
    PLANTBAT: { _id: 'mock-def-plantbat', description: 'Plant Batch' },
    PRUNE:    { _id: 'mock-def-prune',    description: 'Prune Plant' },
    PLANTINS: { _id: 'mock-def-plantins', description: 'Plant Inspection' },
    LABEL:    { _id: 'mock-def-label',    description: 'Label Printing' }
};

// Per-definition target counts (sum = 53). Tune these to change the tile
// distribution; the widget renders tiles in insertion order with the
// matching count, so order here also controls visual order on screen.
const DEF_COUNTS = [
    [DEFS.HARVEST,  7],
    [DEFS.TRANSPL,  4],
    [DEFS.ROOMXFR,  11],
    [DEFS.CLONEINS, 3],
    [DEFS.PLANTBAT, 2],
    [DEFS.PRUNE,    12],
    [DEFS.PLANTINS, 11],
    [DEFS.LABEL,    3]
];

const ASSIGNEES = [
    { firstName: 'Asha',  lastName: 'Patel' },
    { firstName: 'Marc',  lastName: 'Ouellette' },
    { firstName: 'Priya', lastName: 'Singh' },
    { firstName: 'Devon', lastName: 'Yamada' }
];

const makeActivity = (n, def, batch, daysOffset, status, assignee) => ({
    _id: 'mock-hub-a-' + n,
    number: 'BA-2026-04-' + String(200 + n).padStart(4, '0'),
    description: def.description,
    // CultivationHub reads `act.definition` and runs it through
    // displayLabel(val) which expects a string OR a {label|name|text}
    // object. Passing the description string directly is the simplest
    // shape that lets the hub group by definition name.
    definition: def.description,
    plannedDate: daysOffset >= 0 ? daysAhead(daysOffset) : daysAgo(-daysOffset),
    completedDate: null,
    status,
    batch: { _id: batch._id, number: batch.number, strain: batch.strain },
    assignee
});

// Expand DEF_COUNTS into the full activities array. We rotate through the
// available batches and assignees so no single batch/person dominates,
// and spread the planned dates from yesterday through ~3 weeks out.
const mockActivities = (() => {
    const out = [];
    let n = 0;
    DEF_COUNTS.forEach(([def, count]) => {
        for (let i = 0; i < count; i += 1) {
            n += 1;
            const batch = mockBatches[n % mockBatches.length];
            const assignee = ASSIGNEES[n % ASSIGNEES.length];
            const status = n % 5 === 0 ? 'inprogress' : 'new';
            // Spread offsets between -1 (yesterday) and +20 days
            const daysOffset = (n % 22) - 1;
            out.push(makeActivity(n, def, batch, daysOffset, status, assignee));
        }
    });
    return out;
})();

const mockSchedule = [
    { _id: 'mock-hub-s-1', title: 'Defoliation — Flower Rm 1', dueDate: daysAhead(1),
      department: 'cultivation', complete: false, batch: mockBatches[0]._id, batchNumber: mockBatches[0].number },
    { _id: 'mock-hub-s-2', title: 'IPM scouting — Veg Rm 1', dueDate: daysAhead(2),
      department: 'cultivation', complete: false, batch: mockBatches[1]._id, batchNumber: mockBatches[1].number },
    { _id: 'mock-hub-s-3', title: 'Final flush — Flower Rm 2', dueDate: daysAhead(3),
      department: 'cultivation', complete: false, batch: mockBatches[2]._id, batchNumber: mockBatches[2].number },
    { _id: 'mock-hub-s-4', title: 'Clone transplant', dueDate: daysAhead(5),
      department: 'cultivation', complete: false, batch: mockBatches[3]._id, batchNumber: mockBatches[3].number }
];

// DTC = drying / trimming / curing. The hub widget renders three stacked
// bars at the bottom of the page, sized by total grams in each stage.
// To make the bars meaningful we want a mix of in-progress and recently-
// completed orders across multiple prior batches.
const PREV_BATCH_LH = { _id: 'mock-hub-prev-1', number: 'CB-2026-W01-LH', strain: STRAIN_LH };
const PREV_BATCH_BD = { _id: 'mock-hub-prev-2', number: 'CB-2026-W02-BD', strain: STRAIN_BD };
const PREV_BATCH_GG = { _id: 'mock-hub-prev-3', number: 'CB-2026-W01-GG4', strain: STRAIN_GG4 };
const PREV_BATCH_NL = { _id: 'mock-hub-prev-4', number: 'CB-2026-W02-NL', strain: STRAIN_NL };

// Totals tuned to roughly match a real tenant snapshot:
//   Dryings:  5 orders @  ~19,616g total
//   Trimmings: 5 orders @ ~32,841g total
//   Curings:   3 orders @  ~2,897g total
const mockDtc = {
    dryingOrders: [
        { _id: 'mock-hub-d-1', number: 'DRY-2026-W04', status: { name: 'In Progress' },
          batch: PREV_BATCH_LH, startDate: daysAgo(3), endDate: null,          remainingAmount:4280 },
        { _id: 'mock-hub-d-2', number: 'DRY-2026-W03', status: { name: 'In Progress' },
          batch: PREV_BATCH_BD, startDate: daysAgo(6), endDate: null,          remainingAmount:3960 },
        { _id: 'mock-hub-d-3', number: 'DRY-2026-W02', status: { name: 'Complete' },
          batch: PREV_BATCH_GG, startDate: daysAgo(14), endDate: daysAgo(2),   remainingAmount:3820 },
        { _id: 'mock-hub-d-4', number: 'DRY-2026-W01', status: { name: 'Complete' },
          batch: PREV_BATCH_NL, startDate: daysAgo(20), endDate: daysAgo(7),   remainingAmount:4040 },
        { _id: 'mock-hub-d-5', number: 'DRY-2026-W01b', status: { name: 'Complete' },
          batch: PREV_BATCH_LH, startDate: daysAgo(22), endDate: daysAgo(9),   remainingAmount:3516 }
    ],
    trimmingOrders: [
        { _id: 'mock-hub-t-1', number: 'TRM-2026-W03', status: { name: 'Complete' },
          batch: PREV_BATCH_BD, startDate: daysAgo(7), endDate: daysAgo(5),    remainingAmount:6840 },
        { _id: 'mock-hub-t-2', number: 'TRM-2026-W02', status: { name: 'In Progress' },
          batch: PREV_BATCH_GG, startDate: daysAgo(2), endDate: null,          remainingAmount:6720 },
        { _id: 'mock-hub-t-3', number: 'TRM-2026-W02b', status: { name: 'Complete' },
          batch: PREV_BATCH_NL, startDate: daysAgo(6), endDate: daysAgo(4),    remainingAmount:6480 },
        { _id: 'mock-hub-t-4', number: 'TRM-2026-W01', status: { name: 'Complete' },
          batch: PREV_BATCH_LH, startDate: daysAgo(11), endDate: daysAgo(8),   remainingAmount:6420 },
        { _id: 'mock-hub-t-5', number: 'TRM-2026-W01b', status: { name: 'Complete' },
          batch: PREV_BATCH_GG, startDate: daysAgo(15), endDate: daysAgo(13),  remainingAmount:6381 }
    ],
    curings: [
        { _id: 'mock-hub-c-1', number: 'CUR-2026-W02', status: { name: 'In Progress' },
          batch: PREV_BATCH_BD, startDate: daysAgo(5), endDate: null,          remainingAmount:1140 },
        { _id: 'mock-hub-c-2', number: 'CUR-2026-W01', status: { name: 'In Progress' },
          batch: PREV_BATCH_NL, startDate: daysAgo(4), endDate: null,          remainingAmount:980 },
        { _id: 'mock-hub-c-3', number: 'CUR-2026-W01b', status: { name: 'Complete' },
          batch: PREV_BATCH_LH, startDate: daysAgo(12), endDate: daysAgo(8),   remainingAmount:777 }
    ]
};

registerTourMocks(common.screens.cultivationHub, {
    '/api/roomsummary':           () => mockRoomSummary,
    '/api/cultivationbatch/list': () => ({ data: mockBatches, totalCount: mockBatches.length }),
    '/api/batchactivity/list':    () => ({ data: mockActivities, totalCount: mockActivities.length }),
    '/api/schedule/list':         () => ({ data: mockSchedule, totalCount: mockSchedule.length }),
    '/api/dtcprocessing':         () => mockDtc
});

// ---------- Scheduled Activities ----------------------------------------
// Same mock pool — the schedule page reads /api/schedule/list with a
// different date window. For v1 awareness tours we serve the same
// schedule rows regardless of window; the widget visualises them in
// list/calendar form. If the calendar view fetches via a different
// endpoint, register it here too.
// /api/calendarschedules — what the calendar view fetches (different
// endpoint from the list view's /api/schedule/list). Returns a flat
// array of events keyed for FullCalendar-style rendering. The widget
// (`ScheduleCalendar` inside `ScheduleCalendarContainer`) reads each
// event's `_id`, `title`, `dueDate`, `complete`, and batch reference.
const mockCalendarEvents = mockSchedule.map(s => ({
    _id: s._id,
    title: s.title,
    dueDate: s.dueDate,
    start: s.dueDate,
    end: s.dueDate,
    complete: s.complete,
    department: s.department,
    batch: s.batch,
    batchNumber: s.batchNumber,
    allDay: true
}));

registerTourMocks(common.screens.scheduledActivities, {
    '/api/schedule/list':         () => ({ data: mockSchedule, totalCount: mockSchedule.length }),
    '/api/batchactivity/list':    () => ({ data: mockActivities, totalCount: mockActivities.length }),
    '/api/calendarschedules':     () => mockCalendarEvents
});

// ---------- Processing Hub -----------------------------------------------
//
// Endpoints the ProcessingHub component issues (see ProcessingHub.jsx
// around line 419-433 plus the nested ProductionPipeline):
//   GET /api/processinghub/rooms        — { rooms: [{ _id, name, orderCount, equipmentCount, … }] }
//   GET /api/processinghub/production   — { orders, batches, productionIssues, hasForSaleCategories, finishedProductCount }
//   GET /api/schedule/list              — paged schedule entries (this-week window)
//   GET /api/mrp/snapshot               — { shortages, purchaseRequisitions, plannedProductionOrders }
//   GET /api/item/bomexplosion          — flat array of root items w/ children for the pipeline tree
//
// Like the cultivation hub, this is an awareness mock — one coherent dataset
// across all widgets, no per-step variation. Numbers below are tuned to a
// representative mid-size processing tenant so the summary cards, pipeline,
// MRP panel, and orders/alerts table all read as "lived in" rather than empty.

const PROC_VENDOR_PACKCO   = { _id: 'mock-proc-v-packco',   name: 'PackCo Supply' };
const PROC_VENDOR_GLOBE    = { _id: 'mock-proc-v-globe',    name: 'Globe Labels Inc.' };
const PROC_VENDOR_AROMA    = { _id: 'mock-proc-v-aroma',    name: 'Aroma Terpenes Ltd.' };

const PROC_UOM_EA          = { _id: 'mock-proc-uom-ea',  label: 'ea' };
const PROC_UOM_G           = { _id: 'mock-proc-uom-g',   label: 'g' };
const PROC_UOM_ML          = { _id: 'mock-proc-uom-ml',  label: 'ml' };

// --- Processing rooms ---------------------------------------------------
// ProcessingRoomCard reads { _id, name, orderCount, equipmentCount }. The
// hub also derives roomsInUse from orderCount > 0, so the in-use ratio in
// the summary card mirrors how many rooms here have a non-zero count.
const mockProcessingRooms = [
    { _id: 'mock-proc-room-1', name: 'Pre-Roll Suite A',   orderCount: 3, equipmentCount: 4, type: 'preroll' },
    { _id: 'mock-proc-room-2', name: 'Pre-Roll Suite B',   orderCount: 2, equipmentCount: 3, type: 'preroll' },
    { _id: 'mock-proc-room-3', name: 'Packaging Line 1',   orderCount: 4, equipmentCount: 5, type: 'packaging' },
    { _id: 'mock-proc-room-4', name: 'Packaging Line 2',   orderCount: 1, equipmentCount: 5, type: 'packaging' },
    { _id: 'mock-proc-room-5', name: 'Vape Filling',       orderCount: 2, equipmentCount: 6, type: 'vape' },
    { _id: 'mock-proc-room-6', name: 'Extraction Lab',     orderCount: 0, equipmentCount: 8, type: 'extraction' },
    { _id: 'mock-proc-room-7', name: 'QA Hold',            orderCount: 0, equipmentCount: 2, type: 'qa' },
    { _id: 'mock-proc-room-8', name: 'Edibles Kitchen',    orderCount: 0, equipmentCount: 7, type: 'edibles' }
];

// --- Production orders & batches (grouped by status label) --------------
const daysFromNow = (n) => new Date(Date.now() + n * 86400000).toISOString();

const mockProdOrder = (n, name, status, daysOut) => ({
    _id: 'mock-proc-po-' + n,
    number: 'PO-2026-' + String(2000 + n).padStart(4, '0'),
    outputItem: name,
    plannedStart: daysFromNow(daysOut - 2),
    dueDate: daysFromNow(daysOut),
    status
});

const mockProdBatch = (n, name, status, daysOut) => ({
    _id: 'mock-proc-pb-' + n,
    number: 'PB-2026-' + String(3000 + n).padStart(4, '0'),
    outputItem: name,
    plannedStart: daysFromNow(daysOut - 1),
    dueDate: daysFromNow(daysOut),
    status
});

const mockProduction = {
    orders: {
        'New': {
            count: 3,
            orders: [
                mockProdOrder(1, '0.5g Pre-Roll 10pk — LH', 'New', 4),
                mockProdOrder(2, '1g Pre-Roll 3pk — BD',    'New', 6),
                mockProdOrder(3, '3.5g Flower Jar — GG4',   'New', 9)
            ]
        },
        'Released': {
            count: 4,
            orders: [
                mockProdOrder(4, '0.5g Pre-Roll 10pk — GG4', 'Released', 2),
                mockProdOrder(5, '510 Vape 1.0g — LH Live', 'Released', 3),
                mockProdOrder(6, '510 Vape 0.5g — BD Live', 'Released', 5),
                mockProdOrder(7, '7g Flower Jar — NL',      'Released', 7)
            ]
        },
        'In Progress': {
            count: 2,
            orders: [
                mockProdOrder(8, '1g Pre-Roll 3pk — LH', 'In Progress', 1),
                mockProdOrder(9, '3.5g Flower Jar — BD', 'In Progress', 2)
            ]
        }
    },
    batches: {
        'New': {
            count: 2,
            batches: [
                mockProdBatch(1, 'Pre-Roll Mix — LH 2026-W05', 'New', 3),
                mockProdBatch(2, 'Vape Distillate — BD Q1',    'New', 8)
            ]
        },
        'Released': {
            count: 3,
            batches: [
                mockProdBatch(3, 'Pre-Roll Mix — GG4 2026-W04', 'Released', 1),
                mockProdBatch(4, 'Pre-Roll Mix — NL 2026-W04',  'Released', 2),
                mockProdBatch(5, 'Edibles — Gummy Batch 18',    'Released', 4)
            ]
        },
        'In Progress': {
            count: 2,
            batches: [
                mockProdBatch(6, 'Pre-Roll Mix — LH 2026-W04', 'In Progress', 0),
                mockProdBatch(7, 'Vape Distillate — LH Q1',    'In Progress', 1)
            ]
        }
    },
    // Mix of issue types — the table renders chips per issue and an urgent
    // highlight, so include all three signal categories.
    productionIssues: [
        {
            _id: 'mock-proc-iss-1', name: 'Pre-Roll Cone 84mm', number: 'PR-CONE-084',
            issues: ['short_stock'],
            orderNumbers: ['PO-2026-2004'],
            vendors: [PROC_VENDOR_PACKCO],
            reorderLeadTime: 10,
            uom: PROC_UOM_EA, purchaseUOM: PROC_UOM_EA,
            inventory: 2400, available: 1800, reserved: 600,
            minimumQuantity: 5000, deficit: 2600,
            urgent: true
        },
        {
            _id: 'mock-proc-iss-2', name: 'Child-Resistant Tube 116mm', number: 'CR-TUBE-116',
            issues: ['short_stock'],
            orderNumbers: ['PO-2026-2005'],
            vendors: [PROC_VENDOR_PACKCO, PROC_VENDOR_GLOBE],
            reorderLeadTime: 14,
            uom: PROC_UOM_EA, purchaseUOM: PROC_UOM_EA,
            inventory: 1100, available: 900, reserved: 200,
            minimumQuantity: 3000, deficit: 1900,
            urgent: true
        },
        {
            _id: 'mock-proc-iss-3', name: 'Excise Stamp — Provincial', number: 'STAMP-PROV',
            issues: ['short_stock'],
            orderNumbers: ['PO-2026-2001', 'PO-2026-2007'],
            vendors: [],
            reorderLeadTime: 0,
            uom: PROC_UOM_EA, purchaseUOM: PROC_UOM_EA,
            inventory: 380, available: 380, reserved: 0,
            minimumQuantity: 1500, deficit: 1120,
            urgent: false
        },
        {
            _id: 'mock-proc-iss-4', name: '0.5g Pre-Roll 10pk — LH', number: 'FG-PR-LH-05-10',
            issues: ['missing_bom'],
            orderNumbers: ['PO-2026-2001'],
            vendors: [],
            reorderLeadTime: 0,
            uom: PROC_UOM_EA, purchaseUOM: PROC_UOM_EA,
            inventory: 0, available: 0, reserved: 0,
            minimumQuantity: 0, deficit: null,
            urgent: true
        },
        {
            _id: 'mock-proc-iss-5', name: '510 Vape 1.0g — LH Live', number: 'FG-VP-LH-10',
            issues: ['missing_routing'],
            orderNumbers: ['PO-2026-2005'],
            vendors: [],
            reorderLeadTime: 0,
            uom: PROC_UOM_EA, purchaseUOM: PROC_UOM_EA,
            inventory: 0, available: 0, reserved: 0,
            minimumQuantity: 0, deficit: null,
            urgent: true
        },
        {
            _id: 'mock-proc-iss-6', name: 'Terpene Blend — Citrus', number: 'TERP-CITR',
            issues: ['short_stock'],
            orderNumbers: [],
            vendors: [PROC_VENDOR_AROMA],
            reorderLeadTime: 21,
            uom: PROC_UOM_ML, purchaseUOM: PROC_UOM_ML,
            inventory: 240, available: 240, reserved: 0,
            minimumQuantity: 500, deficit: 260,
            urgent: false
        }
    ],
    finishedProductCount: 42,
    hasForSaleCategories: true
};

// --- This-week schedule (drives the 7-day calendar strip) ----------------
const mockProcSchedule = [
    { _id: 'mock-proc-s-1', title: 'Pre-roll machine sanitation',  dueDate: daysFromNow(0), department: 'processing', complete: false,
      batch: 'mock-proc-pb-6', batchNumber: 'PB-2026-3006' },
    { _id: 'mock-proc-s-2', title: 'QA hold release — LH PR',      dueDate: daysFromNow(0), department: 'processing', complete: false,
      batch: 'mock-proc-pb-6', batchNumber: 'PB-2026-3006' },
    { _id: 'mock-proc-s-3', title: 'Vape cartridge fill changeover', dueDate: daysFromNow(1), department: 'processing', complete: false,
      batch: 'mock-proc-pb-7', batchNumber: 'PB-2026-3007' },
    { _id: 'mock-proc-s-4', title: 'Edibles batch start-up check',  dueDate: daysFromNow(2), department: 'processing', complete: false,
      batch: 'mock-proc-pb-5', batchNumber: 'PB-2026-3005' },
    { _id: 'mock-proc-s-5', title: 'Excise stamp reconciliation',   dueDate: daysFromNow(2), department: 'processing', complete: false },
    { _id: 'mock-proc-s-6', title: 'Pack line 2 calibration',       dueDate: daysFromNow(3), department: 'processing', complete: false },
    { _id: 'mock-proc-s-7', title: 'Distillate transfer to filling', dueDate: daysFromNow(4), department: 'processing', complete: false,
      batch: 'mock-proc-pb-2', batchNumber: 'PB-2026-3002' },
    { _id: 'mock-proc-s-8', title: 'Pre-roll weight audit',         dueDate: daysFromNow(5), department: 'processing', complete: false }
];

// --- MRP snapshot --------------------------------------------------------
// Shapes mirror MrpSnapshotView.jsx readers: shortages.{itemNumber,itemName,
// shortQuantity,reason}; requisitions.{itemNumber,itemName,quantity,buyBy,
// neededBy,vendors[].name,leadTime,leadTimeAssumed,source,saleNumber};
// plannedProductionOrders.{itemNumber,itemName,quantity,dueDate,startBy,
// source,saleNumber}.
const mockMrpSnapshot = {
    shortages: [
        { itemId: 'mock-proc-iss-1', itemNumber: 'PR-CONE-084', itemName: 'Pre-Roll Cone 84mm',
          shortQuantity: 2600, reason: 'Net of inventory and reservations falls below committed demand for PO-2026-2004.' },
        { itemId: 'mock-proc-iss-2', itemNumber: 'CR-TUBE-116', itemName: 'Child-Resistant Tube 116mm',
          shortQuantity: 1900, reason: 'Below minimum safety stock with two open vendors.' },
        { itemId: 'mock-proc-iss-6', itemNumber: 'TERP-CITR',   itemName: 'Terpene Blend — Citrus',
          shortQuantity: 260,  reason: 'Below minimum; 21-day lead time on sole vendor.' }
    ],
    purchaseRequisitions: [
        { itemId: 'mock-proc-iss-1', itemNumber: 'PR-CONE-084', itemName: 'Pre-Roll Cone 84mm',
          quantity: 5000, buyBy: daysFromNow(-1), neededBy: daysFromNow(9),
          vendors: [PROC_VENDOR_PACKCO], leadTime: 10, leadTimeAssumed: false, source: 'schedule' },
        { itemId: 'mock-proc-iss-2', itemNumber: 'CR-TUBE-116', itemName: 'Child-Resistant Tube 116mm',
          quantity: 3000, buyBy: daysFromNow(2), neededBy: daysFromNow(16),
          vendors: [PROC_VENDOR_PACKCO, PROC_VENDOR_GLOBE], leadTime: 14, leadTimeAssumed: false, source: 'schedule' },
        { itemId: 'mock-proc-iss-6', itemNumber: 'TERP-CITR', itemName: 'Terpene Blend — Citrus',
          quantity: 1000, buyBy: daysFromNow(5), neededBy: daysFromNow(26),
          vendors: [PROC_VENDOR_AROMA], leadTime: 21, leadTimeAssumed: true, source: 'sale', saleNumber: 'SO-2026-0414' },
        { itemId: 'mock-proc-iss-3', itemNumber: 'STAMP-PROV', itemName: 'Excise Stamp — Provincial',
          quantity: 2000, buyBy: daysFromNow(7), neededBy: daysFromNow(7),
          vendors: [], leadTime: null, leadTimeAssumed: true, source: 'schedule' }
    ],
    plannedProductionOrders: [
        { itemId: 'mock-proc-fg-pr-lh-10', itemNumber: 'FG-PR-LH-05-10', itemName: '0.5g Pre-Roll 10pk — LH',
          quantity: 800, dueDate: daysFromNow(7), startBy: daysFromNow(3), source: 'sale', saleNumber: 'SO-2026-0420' },
        { itemId: 'mock-proc-fg-vp-bd-05', itemNumber: 'FG-VP-BD-05', itemName: '510 Vape 0.5g — BD Live',
          quantity: 400, dueDate: daysFromNow(11), startBy: daysFromNow(5), source: 'schedule' },
        { itemId: 'mock-proc-fg-jar-gg-35', itemNumber: 'FG-JAR-GG-35', itemName: '3.5g Flower Jar — GG4',
          quantity: 600, dueDate: daysFromNow(14), startBy: daysFromNow(7), source: 'sale', saleNumber: 'SO-2026-0422' }
    ]
};

// --- BOM explosion (drives the ProductionPipeline tree) ------------------
// Root rows = finished goods; children = their BOM components. The pipeline
// renders depth via colour banding and indent — two levels is enough to
// show off the expand/collapse + per-node action chips.
const mockBomExplosion = [
    {
        _id: 'mock-proc-fg-pr-lh-10', label: '0.5g Pre-Roll 10pk — LH',
        category: 'Pre-Roll', uom: 'ea', uomId: PROC_UOM_EA._id,
        onHand: 240, couldMake: 180, isPurchased: false, hot: true, velocityDays: 6,
        children: [
            { _id: 'mock-proc-bom-flowerlh', label: 'Milled Flower — LH', category: 'Bulk Flower',
              uom: 'g', uomId: PROC_UOM_G._id, qtyPerParent: 5, onHand: 4800, couldMake: 0,
              isPurchased: false, velocityDays: 14 },
            { _id: 'mock-proc-iss-1', label: 'Pre-Roll Cone 84mm', category: 'Packaging',
              uom: 'ea', uomId: PROC_UOM_EA._id, qtyPerParent: 10, onHand: 2400, couldMake: 0,
              isPurchased: true, vendors: [PROC_VENDOR_PACKCO], velocityDays: 28 },
            { _id: 'mock-proc-iss-2', label: 'Child-Resistant Tube 116mm', category: 'Packaging',
              uom: 'ea', uomId: PROC_UOM_EA._id, qtyPerParent: 1, onHand: 1100, couldMake: 0,
              isPurchased: true, vendors: [PROC_VENDOR_PACKCO, PROC_VENDOR_GLOBE], velocityDays: 32 }
        ]
    },
    {
        _id: 'mock-proc-fg-vp-lh-10', label: '510 Vape 1.0g — LH Live',
        category: 'Vape', uom: 'ea', uomId: PROC_UOM_EA._id,
        onHand: 96, couldMake: 60, isPurchased: false, hot: true, velocityDays: 9,
        children: [
            { _id: 'mock-proc-bom-distlh', label: 'Distillate — LH Live Resin', category: 'Concentrate',
              uom: 'ml', uomId: PROC_UOM_ML._id, qtyPerParent: 1, onHand: 320, couldMake: 0,
              isPurchased: false, velocityDays: 18 },
            { _id: 'mock-proc-iss-6', label: 'Terpene Blend — Citrus', category: 'Aromatics',
              uom: 'ml', uomId: PROC_UOM_ML._id, qtyPerParent: 0.08, onHand: 240, couldMake: 0,
              isPurchased: true, vendors: [PROC_VENDOR_AROMA], velocityDays: 35 },
            { _id: 'mock-proc-bom-cart', label: '510 Cartridge 1.0ml', category: 'Hardware',
              uom: 'ea', uomId: PROC_UOM_EA._id, qtyPerParent: 1, onHand: 1800, couldMake: 0,
              isPurchased: true, vendors: [PROC_VENDOR_PACKCO], velocityDays: 22 }
        ]
    },
    {
        _id: 'mock-proc-fg-jar-gg-35', label: '3.5g Flower Jar — GG4',
        category: 'Flower', uom: 'ea', uomId: PROC_UOM_EA._id,
        onHand: 168, couldMake: 240, isPurchased: false, velocityDays: 12,
        children: [
            { _id: 'mock-proc-bom-flowergg', label: 'Cured Flower — GG4', category: 'Bulk Flower',
              uom: 'g', uomId: PROC_UOM_G._id, qtyPerParent: 3.5, onHand: 6200, couldMake: 0,
              isPurchased: false, velocityDays: 16 },
            { _id: 'mock-proc-bom-jar60',  label: 'Glass Jar 60ml CR', category: 'Packaging',
              uom: 'ea', uomId: PROC_UOM_EA._id, qtyPerParent: 1, onHand: 2200, couldMake: 0,
              isPurchased: true, vendors: [PROC_VENDOR_PACKCO], velocityDays: 30 },
            { _id: 'mock-proc-iss-3', label: 'Excise Stamp — Provincial', category: 'Compliance',
              uom: 'ea', uomId: PROC_UOM_EA._id, qtyPerParent: 1, onHand: 380, couldMake: 0,
              isPurchased: true, vendors: [], velocityDays: 45 }
        ]
    },
    {
        _id: 'mock-proc-fg-edib-gum', label: 'Gummy 10pk — Mixed Berry',
        category: 'Edibles', uom: 'ea', uomId: PROC_UOM_EA._id,
        onHand: 320, couldMake: 80, isPurchased: false, velocityDays: 20,
        children: [
            { _id: 'mock-proc-bom-dist-edi', label: 'Distillate — Indica Blend', category: 'Concentrate',
              uom: 'ml', uomId: PROC_UOM_ML._id, qtyPerParent: 0.4, onHand: 180, couldMake: 0,
              isPurchased: false, velocityDays: 24 },
            { _id: 'mock-proc-bom-pectin', label: 'Pectin Base', category: 'Ingredient',
              uom: 'g', uomId: PROC_UOM_G._id, qtyPerParent: 12, onHand: 4800, couldMake: 0,
              isPurchased: true, vendors: [PROC_VENDOR_AROMA], velocityDays: 40 }
        ]
    }
];

registerTourMocks(common.screens.processingHub, {
    '/api/processinghub/rooms':      () => ({ rooms: mockProcessingRooms }),
    '/api/processinghub/production': () => mockProduction,
    '/api/schedule/list':            () => ({ data: mockProcSchedule, totalCount: mockProcSchedule.length }),
    '/api/mrp/snapshot':             () => mockMrpSnapshot,
    '/api/item/bomexplosion':        () => mockBomExplosion
});
